<template>
  <div class="tw-p-4 tw-pt-0">
    <!-- Page Header -->
    <div class="tw-mb-6">
      <h1 class="tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-2">Sessions & Events</h1>
      <p class="tw-text-gray-600">Browse and join upcoming sessions, webinars, and events</p>
    </div>

    <!-- Filters -->
    <SessionFilters v-if="false"
      :filters="filters"
      @update-filters="handleFiltersUpdate"
      @reset="handleFiltersReset"
      class="tw-mb-6"
    />

    <!-- Loading State for Initial Load -->
    <div v-if="initialLoading" class="tw-space-y-6">
      <div class="tw-bg-whitex tw-rounded-lgx tw-borderx tw-border-gray-200x tw-p-6x">
        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
          <SessionLoadingSkeleton v-for="i in 8" :key="i" />
        </div>
      </div>
    </div>

    <!-- Consolidated Sessions List -->
    <div v-else>
      <SessionList
        title="All Events"
        :sessions="consolidatedSessions"
        :loading="false"
        :has-more="false"
        category="all"
        :loading-sessions="loadingSessions"
        @load-more="handleLoadMore"
        @session-join="handleSessionJoin"
        @session-view="handleSessionView"
        @empty-state-action="handleEmptyStateAction"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import moment from 'moment'
import { paginate, join } from '@/api/sessions'
import useAlert from '@/composables/useAlert'
import {
  SessionList,
  SessionFilters,
  SessionLoadingSkeleton
} from '@/components/sessions'

// Reactive data
const initialLoading = ref(true)
const allSessions = ref([])
const filters = ref({
  search: '',
  startDate: '',
  endDate: '',
  quickFilter: ''
})

const loadingStates = ref({
  today: false,
  upcoming: false,
  past: false
})

const hasMore = ref({
  today: false,
  upcoming: false,
  past: false
})

const paginationData = ref({})
const loadingSessions = ref(new Set())

// Computed properties - Consolidated list with proper ordering
const consolidatedSessions = computed(() => {
  const categorized = categorizeSessions(allSessions.value)
  
  // Create a single list with proper ordering: Live → Upcoming → Past/Cancelled
  const consolidated = []
  
  // 1. Live events first (currently happening)
  const liveEvents = categorized.live || []
  consolidated.push(...liveEvents)
  
  // 2. Today's events (if not already included in live)
  const todayEvents = categorized.today.filter(event => 
    !liveEvents.some(live => live.id === event.id)
  )
  consolidated.push(...todayEvents)
  
  // 3. Upcoming events
  consolidated.push(...categorized.upcoming)
  
  // 4. Past events
  consolidated.push(...categorized.past)
  
  return consolidated
})

// Helper functions

const categorizeSessions = (sessions) => {
  const now = moment()
  const today = moment().startOf('day')
  
  const categorized = {
    live: [],
    today: [],
    upcoming: [],
    past: []
  }
  
  sessions.forEach(session => {
    const sessionStart = moment(`${session.start_date} ${session.start_time}`, 'YYYY-MM-DD HH:mm')
    const sessionEnd = moment(`${session.start_date} ${session.end_time}`, 'YYYY-MM-DD HH:mm')
    
    // Check if session is currently live (happening now)
    if (now.isBetween(sessionStart, sessionEnd, null, '[]')) {
      categorized.live.push(session)
    }
    // Check if session is today
    else if (sessionStart.isSame(today, 'day')) {
      categorized.today.push(session)
    }
    // Check if session is upcoming
    else if (sessionStart.isAfter(now)) {
      categorized.upcoming.push(session)
    }
    // Session is in the past
    else {
      categorized.past.push(session)
    }
  })
  
  // Sort sessions within each category by date and time
  Object.keys(categorized).forEach(category => {
    categorized[category].sort((a, b) => {
      const dateA = moment(`${a.start_date} ${a.start_time}`)
      const dateB = moment(`${b.start_date} ${b.start_time}`)
      return dateA.isBefore(dateB) ? -1 : 1
    })
  })
  
  return categorized
}

const filterSessions = (sessions) => {
  let filtered = [...sessions]
  
  // Search filter
  if (filters.value.search) {
    const searchTerm = filters.value.search.toLowerCase()
    filtered = filtered.filter(session =>
      session.name.toLowerCase().includes(searchTerm) ||
      session.description.toLowerCase().includes(searchTerm) ||
      session.topic.toLowerCase().includes(searchTerm) ||
      session.language.toLowerCase().includes(searchTerm)
    )
  }
  
  // Date filter
  if (filters.value.startDate || filters.value.endDate) {
    filtered = filtered.filter(session => {
      const sessionDate = moment(session.start_date)
      const startDate = filters.value.startDate ? moment(filters.value.startDate) : null
      const endDate = filters.value.endDate ? moment(filters.value.endDate) : null
      
      if (startDate && endDate) {
        return sessionDate.isBetween(startDate, endDate, 'day', '[]')
      } else if (startDate) {
        return sessionDate.isSameOrAfter(startDate, 'day')
      } else if (endDate) {
        return sessionDate.isSameOrBefore(endDate, 'day')
      }
      
      return true
    })
  }
  
  return filtered
}

// API functions
const fetchSessions = async (category = null) => {
  try {
    const params = {
      page: 1,
      per_page: 50 // Fetch more to allow for client-side filtering
    }
    
    if (category) {
      loadingStates.value[category] = true
    } else {
      initialLoading.value = true
    }
    
    const response = await paginate(params)
    const { data, ...otherItems } = response.data
    paginationData.value = otherItems
    
    // Apply filters to the fetched data
    const filteredData = filterSessions(data)
    allSessions.value = filteredData
    
    // Update pagination info
    hasMore.value = {
      today: false,
      upcoming: false,
      past: false
    }
    
  } catch (error) {
    console.error('Error fetching sessions:', error)
    useAlert('Failed to load sessions. Please try again.', 'danger')
  } finally {
    if (category) {
      loadingStates.value[category] = false
    } else {
      initialLoading.value = false
    }
  }
}

// Event handlers
const handleFiltersUpdate = (newFilters) => {
  filters.value = { ...newFilters }
  // Refetch sessions with new filters
  fetchSessions()
}

const handleFiltersReset = () => {
  filters.value = {
    search: '',
    startDate: '',
    endDate: '',
    quickFilter: ''
  }
  // Refetch sessions without filters
  fetchSessions()
}

const handleLoadMore = async (category) => {
  try {
    // For the consolidated list, we'll show a general message
    if (category === 'all') {
      useAlert('Loading more events...', 'info')
    } else {
      loadingStates.value[category] = true
      useAlert(`Loading more ${category} events...`, 'info')
    }
    
    // For now, we'll just show a message since we're doing client-side filtering
    // In a real implementation, you might want to implement server-side pagination
    // Simulate loading more data
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    if (category === 'all') {
      useAlert('No more events to load.', 'info')
    } else {
      hasMore.value[category] = false
      useAlert(`No more ${category} events to load.`, 'info')
    }
    
  } catch (error) {
    console.error('Error loading more sessions:', error)
    useAlert('Failed to load more sessions.', 'danger')
  } finally {
    if (category !== 'all') {
      loadingStates.value[category] = false
    }
  }
}

const handleSessionJoin = async (session) => {
  loadingSessions.value.add(session.id)
  try {
    // Call the join API to toggle join status
    await join(session.id)
    const action = session.is_joined ? 'left' : 'joined'
    useAlert(`Successfully ${action} "${session.name}"`, 'success')
    
    // Update the session's join status in local state
    const sessionIndex = allSessions.value.findIndex(s => s.id === session.id)
    if (sessionIndex !== -1) {
      allSessions.value[sessionIndex].is_joined = !allSessions.value[sessionIndex].is_joined
    }
  } catch (error) {
    console.error('Error toggling session join:', error)
    
    // Handle specific 422 error (session has ended)
    if (error.response && error.response.status === 422) {
      const errorMessage = error.response.data?.message || 'This session has already ended.'
      useAlert(errorMessage, 'warning')
    } else {
      const action = session.is_joined ? 'leave' : 'join'
      useAlert(`Failed to ${action} session. Please try again.`, 'danger')
    }
  } finally {
    loadingSessions.value.delete(session.id)
  }
}

const handleSessionView = (session) => {
  useAlert(`Viewing details for "${session.name}"`, 'info')
  // In a real implementation, you might navigate to a detail page
  console.log('View session:', session)
}

const handleEmptyStateAction = (category) => {
  if (category === 'all') {
    useAlert('Action for all events', 'info')
  } else {
    useAlert(`Action for ${category} category`, 'info')
  }
  // In a real implementation, you might show a form to create new events
  console.log('Empty state action for category:', category)
}

// Watch for filter changes
watch(filters, () => {
  // Refetch sessions when filters change
  fetchSessions()
}, { deep: true })

// Lifecycle
onMounted(() => {
  fetchSessions()
})
</script>